import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, RefreshCw, LogOut, Loader2, Download, Trash2, Youtube, Share2 } from "lucide-react";
import { Logo, Wordmark } from "@/components/landing/Logo";
import { BillingTab } from "@/components/admin/BillingTab";
import { MagazineTab } from "@/components/admin/MagazineTab";
import { AdsTab } from "@/components/admin/AdsTab";
import { AeoTab } from "@/components/admin/AeoTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANG_LABELS = { de: "Deutsch", en: "English", es: "Español", it: "Italiano", fr: "Français", unbekannt: "Unbekannt" };

export default function Admin() {
  const [key, setKey] = useState(() => sessionStorage.getItem("mira-admin-key") || "");
  const [input, setInput] = useState("");
  const [tab, setTab] = useState("stats");
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState(null);
  const [subs, setSubs] = useState(null);
  const [contacts, setContacts] = useState(null);
  const [chats, setChats] = useState(null);
  const [openChat, setOpenChat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (k) => {
    setLoading(true);
    setError("");
    try {
      const headers = { "X-Admin-Key": k };
      const [l, s, c, ch, st] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers }),
        axios.get(`${API}/admin/newsletter`, { headers }),
        axios.get(`${API}/admin/contacts`, { headers }),
        axios.get(`${API}/admin/chats`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setLeads(l.data);
      setSubs(s.data);
      setContacts(c.data);
      setChats(ch.data);
      setStats(st.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem("mira-admin-key");
        setKey("");
        setError("Ungültiger Admin-Key.");
      } else {
        setError("Daten konnten nicht geladen werden.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (key) load(key);
  }, [key, load]);

  useEffect(() => {
    if (tab !== "stats" || !key) return;
    const iv = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/admin/stats`, { headers: { "X-Admin-Key": key } });
        setStats(r.data);
      } catch {}
    }, 30000);
    return () => clearInterval(iv);
  }, [tab, key]);

  const login = (e) => {
    e.preventDefault();
    sessionStorage.setItem("mira-admin-key", input);
    setKey(input);
  };

  const logout = () => {
    sessionStorage.removeItem("mira-admin-key");
    setKey("");
    setLeads(null);
    setSubs(null);
    setContacts(null);
    setChats(null);
    setStats(null);
  };

  const fmt = (iso) => new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });

  const toggleAnswered = async (kind, id, value) => {
    try {
      await axios.patch(`${API}/admin/${kind}/${id}`, { answered: value }, { headers: { "X-Admin-Key": key } });
      if (kind === "leads") setLeads((d) => ({ ...d, leads: d.leads.map((x) => (x.id === id ? { ...x, answered: value } : x)) }));
      else setContacts((d) => ({ ...d, contacts: d.contacts.map((x) => (x.id === id ? { ...x, answered: value } : x)) }));
    } catch {
      setError("Status konnte nicht gespeichert werden.");
    }
  };

  const deleteItem = async (kind, id) => {
    if (!window.confirm("Diesen Eintrag endgültig löschen?")) return;
    try {
      await axios.delete(`${API}/admin/${kind}/${id}`, { headers: { "X-Admin-Key": key } });
      if (kind === "leads") setLeads((d) => ({ ...d, count: d.count - 1, leads: d.leads.filter((x) => x.id !== id) }));
      else if (kind === "contacts") setContacts((d) => ({ ...d, count: d.count - 1, contacts: d.contacts.filter((x) => x.id !== id) }));
      else setSubs((d) => ({ ...d, count: d.count - 1, subscribers: d.subscribers.filter((x) => x.id !== id) }));
    } catch {
      setError("Eintrag konnte nicht gelöscht werden.");
    }
  };

  const TrashBtn = ({ kind, id }) => (
    <button
      data-testid={`delete-btn-${id}`}
      onClick={() => deleteItem(kind, id)}
      title="Eintrag löschen"
      className="flex h-8 w-8 items-center justify-center rounded-full border transition-colors duration-300 hover:border-[var(--rose)] hover:text-[var(--rose)]"
      style={{ borderColor: "var(--line)", color: "var(--muted-warm)" }}
    >
      <Trash2 size={14} />
    </button>
  );

  const StatusPill = ({ kind, item }) => (
    <button
      data-testid={`status-toggle-${item.id}`}
      onClick={() => toggleAnswered(kind, item.id, !item.answered)}
      title={item.answered ? "Als offen markieren" : "Als beantwortet markieren"}
      className="whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors duration-300"
      style={
        item.answered
          ? { background: "var(--rose-soft)", borderColor: "var(--gold)", color: "var(--camel)" }
          : { background: "transparent", borderColor: "var(--line)", color: "var(--muted-warm)" }
      }
    >
      {item.answered ? "✦ Beantwortet" : "Offen"}
    </button>
  );

  const downloadCSV = (filename, headers, rows) => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [headers.join(";"), ...rows.map((r) => r.map(esc).join(";"))].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportCurrent = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    if (tab === "leads" && leads) {
      downloadCSV(
        `mira-leads-${stamp}.csv`,
        ["Datum", "Name", "Boutique", "E-Mail", "Plan", "Sprache", "Nachricht", "Status"],
        leads.leads.map((l) => [l.created_at, l.name, l.boutique, l.email, l.plan || "", l.lang || "", l.message, l.answered ? "Beantwortet" : "Offen"])
      );
    } else if (tab === "newsletter" && subs) {
      downloadCSV(
        `mira-newsletter-${stamp}.csv`,
        ["Datum", "E-Mail"],
        subs.subscribers.map((s) => [s.created_at, s.email])
      );
    } else if (tab === "contacts" && contacts) {
      downloadCSV(
        `mira-kontaktanfragen-${stamp}.csv`,
        ["Datum", "Name", "E-Mail", "Betreff", "Sprache", "Nachricht", "Status"],
        contacts.contacts.map((c) => [c.created_at, c.name, c.email, c.subject || "", c.lang || "", c.message, c.answered ? "Beantwortet" : "Offen"])
      );
    } else if (tab === "chats" && chats) {
      const rows = [];
      chats.chats.forEach((c) => c.messages.forEach((m) => rows.push([m.created_at, c.session_id, c.lang || "", m.role, m.content])));
      downloadCSV(`mira-chats-${stamp}.csv`, ["Datum", "Session", "Sprache", "Rolle", "Nachricht"], rows);
    } else if (tab === "stats" && stats) {
      downloadCSV(
        `mira-top-fragen-${stamp}.csv`,
        ["Frage", "Anzahl"],
        stats.top_questions.map((q) => [q.question, q.count])
      );
    }
  };

  const visibleLeads = leads ? (onlyOpen ? leads.leads.filter((l) => !l.answered) : leads.leads) : [];
  const visibleContacts = contacts ? (onlyOpen ? contacts.contacts.filter((c) => !c.answered) : contacts.contacts) : [];
  const openCount = (leads ? leads.leads.filter((l) => !l.answered).length : 0) + (contacts ? contacts.contacts.filter((c) => !c.answered).length : 0);

  if (!key) {
    return (
      <main className="flex min-h-screen items-center justify-center px-5" style={{ background: "var(--surface)" }}>
        <form onSubmit={login} className="w-full max-w-sm rounded-3xl border p-9 text-center" style={{ borderColor: "var(--line)", background: "var(--surface-secondary)" }} data-testid="admin-login-form">
          <div className="flex justify-center"><Logo size={44} /></div>
          <h1 className="mt-5 font-serif-display text-3xl font-semibold">Admin</h1>
          <p className="mt-2 text-sm" style={{ color: "var(--muted-warm)" }}>Zugriff nur mit Admin-Key</p>
          <input
            data-testid="admin-key-input"
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Admin-Key"
            className="mt-6 w-full rounded-full border bg-white px-5 py-3 text-sm outline-none focus:border-[var(--camel)]"
            style={{ borderColor: "var(--line)" }}
          />
          {error && <p data-testid="admin-login-error" className="mt-3 text-sm" style={{ color: "var(--rose)" }}>{error}</p>}
          <button data-testid="admin-login-btn" type="submit" className="pill-btn mt-5 w-full rounded-full py-3 text-sm font-medium text-white" style={{ background: "var(--camel)" }}>
            Anmelden
          </button>
          <Link to="/" className="mt-5 inline-flex items-center gap-1.5 text-xs" style={{ color: "var(--muted-warm)" }}>
            <ArrowLeft size={12} /> Zur Startseite
          </Link>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen" style={{ background: "var(--surface)", color: "var(--on-surface)" }} data-testid="admin-dashboard">
      <header className="border-b" style={{ borderColor: "var(--line)" }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <Link to="/" className="flex items-center gap-3">
            <Logo size={34} />
            <Wordmark className="text-sm" />
            <span className="rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase text-white" style={{ background: "var(--camel)" }}>Admin</span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-pinterest-connect-btn"
              onClick={() => window.open(`${API}/auth/pinterest/start?key=${encodeURIComponent(key)}`, "_blank")}
              className="flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-medium transition-colors hover:border-[var(--camel)]"
              style={{ borderColor: "var(--line)" }}
            >
              <Share2 size={13} /> Pinterest verbinden
            </button>
            <button
              data-testid="admin-youtube-connect-btn"
              onClick={() => window.open(`${API}/auth/youtube/start?key=${encodeURIComponent(key)}`, "_blank")}
              className="flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-medium transition-colors hover:border-[var(--camel)]"
              style={{ borderColor: "var(--line)" }}
            >
              <Youtube size={13} /> YouTube verbinden
            </button>
            <button data-testid="admin-refresh-btn" onClick={() => load(key)} className="flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-medium transition-colors hover:border-[var(--camel)]" style={{ borderColor: "var(--line)" }}>
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Aktualisieren
            </button>
            <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-medium transition-colors hover:border-[var(--rose)]" style={{ borderColor: "var(--line)" }}>
              <LogOut size={13} /> Abmelden
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-10">
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="admin-tab-stats"
            onClick={() => setTab("stats")}
            className="rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300"
            style={{ background: tab === "stats" ? "var(--camel)" : "var(--surface-secondary)", color: tab === "stats" ? "#fff" : "var(--on-surface)" }}
          >
            ✦ Statistik
          </button>
          <button
            data-testid="admin-tab-leads"
            onClick={() => setTab("leads")}
            className="rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300"
            style={{ background: tab === "leads" ? "var(--camel)" : "var(--surface-secondary)", color: tab === "leads" ? "#fff" : "var(--on-surface)" }}
          >
            Boutique-Anfragen {leads ? `(${leads.count})` : ""}
          </button>
          <button
            data-testid="admin-tab-newsletter"
            onClick={() => setTab("newsletter")}
            className="rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300"
            style={{ background: tab === "newsletter" ? "var(--camel)" : "var(--surface-secondary)", color: tab === "newsletter" ? "#fff" : "var(--on-surface)" }}
          >
            Newsletter {subs ? `(${subs.count})` : ""}
          </button>
          <button
            data-testid="admin-tab-contacts"
            onClick={() => setTab("contacts")}
            className="rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300"
            style={{ background: tab === "contacts" ? "var(--camel)" : "var(--surface-secondary)", color: tab === "contacts" ? "#fff" : "var(--on-surface)" }}
          >
            Kontaktanfragen {contacts ? `(${contacts.count})` : ""}
          </button>
          <button
            data-testid="admin-tab-chats"
            onClick={() => setTab("chats")}
            className="rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300"
            style={{ background: tab === "chats" ? "var(--camel)" : "var(--surface-secondary)", color: tab === "chats" ? "#fff" : "var(--on-surface)" }}
          >
            Chats {chats ? `(${chats.count})` : ""}
          </button>
          <button
            data-testid="admin-tab-magazine"
            onClick={() => setTab("magazine")}
            className="rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300"
            style={{ background: tab === "magazine" ? "var(--camel)" : "var(--surface-secondary)", color: tab === "magazine" ? "#fff" : "var(--on-surface)" }}
          >
            Magazin
          </button>
          <button
            data-testid="admin-tab-ads"
            onClick={() => setTab("ads")}
            className="rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300"
            style={{ background: tab === "ads" ? "var(--camel)" : "var(--surface-secondary)", color: tab === "ads" ? "#fff" : "var(--on-surface)" }}
          >
            Werbung
          </button>
          <button
            data-testid="admin-tab-aeo"
            onClick={() => setTab("aeo")}
            className="rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300"
            style={{ background: tab === "aeo" ? "var(--camel)" : "var(--surface-secondary)", color: tab === "aeo" ? "#fff" : "var(--on-surface)" }}
          >
            KI-Sichtbarkeit
          </button>
          <button
            data-testid="admin-tab-billing"
            onClick={() => setTab("billing")}
            className="rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300"
            style={{ background: tab === "billing" ? "var(--camel)" : "var(--surface-secondary)", color: tab === "billing" ? "#fff" : "var(--on-surface)" }}
          >
            Abrechnung
          </button>
          {(tab === "leads" || tab === "contacts") && (
            <button
              data-testid="admin-filter-open-btn"
              onClick={() => setOnlyOpen((o) => !o)}
              className="flex items-center gap-1.5 rounded-full border px-4 py-2.5 text-sm font-medium transition-colors duration-300"
              style={
                onlyOpen
                  ? { background: "var(--rose-soft)", borderColor: "var(--gold)", color: "var(--camel)" }
                  : { background: "transparent", borderColor: "var(--line)", color: "var(--muted-warm)" }
              }
            >
              {onlyOpen ? "✦ " : ""}Nur offene ({openCount})
            </button>
          )}
          <button
            data-testid="admin-export-csv-btn"
            onClick={exportCurrent}
            className="ml-auto flex items-center gap-1.5 rounded-full border px-5 py-2.5 text-sm font-medium transition-colors duration-300 hover:border-[var(--camel)] hover:text-[var(--camel)]"
            style={{ borderColor: "var(--gold)", color: "var(--on-surface)" }}
          >
            <Download size={15} /> CSV exportieren
          </button>
        </div>

        {loading && !leads && (
          <div className="mt-16 flex justify-center"><Loader2 size={28} className="animate-spin" style={{ color: "var(--camel)" }} /></div>
        )}
        {error && key && <p className="mt-6 text-sm" style={{ color: "var(--rose)" }}>{error}</p>}

        {tab === "stats" && stats && (
          <div className="mt-7" data-testid="admin-stats-panel">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-2xl border p-6" style={{ borderColor: "var(--gold)", background: "var(--surface-secondary)" }} data-testid="stat-waitlist">
                <div className="flex items-center gap-2 text-xs font-bold uppercase" style={{ letterSpacing: "0.1em", color: "var(--camel)" }}>
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60" style={{ background: "var(--camel)" }} />
                    <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: "var(--camel)" }} />
                  </span>
                  Warteliste · Live
                </div>
                <p className="mt-3 font-serif-display text-5xl font-semibold" data-testid="stat-waitlist-count">{stats.newsletter_count}</p>
                <p className="mt-1 text-xs" style={{ color: "var(--muted-warm)" }}>Newsletter-Anmeldungen · aktualisiert alle 30 s</p>
              </div>
              <div className="rounded-2xl border p-6" style={{ borderColor: "var(--line)" }} data-testid="stat-leads">
                <p className="text-xs font-bold uppercase" style={{ letterSpacing: "0.1em", color: "var(--muted-warm)" }}>Boutique-Anfragen</p>
                <p className="mt-3 font-serif-display text-5xl font-semibold">{stats.leads_count}</p>
              </div>
              <div className="rounded-2xl border p-6" style={{ borderColor: "var(--line)" }} data-testid="stat-contacts">
                <p className="text-xs font-bold uppercase" style={{ letterSpacing: "0.1em", color: "var(--muted-warm)" }}>Kontaktanfragen</p>
                <p className="mt-3 font-serif-display text-5xl font-semibold">{stats.contacts_count}</p>
              </div>
              <div className="rounded-2xl border p-6" style={{ borderColor: "var(--line)" }} data-testid="stat-chats">
                <p className="text-xs font-bold uppercase" style={{ letterSpacing: "0.1em", color: "var(--muted-warm)" }}>Chat-Gespräche</p>
                <p className="mt-3 font-serif-display text-5xl font-semibold">{stats.chat_sessions}</p>
                <p className="mt-1 text-xs" style={{ color: "var(--muted-warm)" }}>{stats.chat_user_messages} Fragen insgesamt</p>
              </div>
            </div>

            <div className="mt-8 grid gap-6 lg:grid-cols-2">
              <div className="rounded-2xl border p-6" style={{ borderColor: "var(--line)" }} data-testid="stats-topics">
                <h2 className="font-serif-display text-xl font-semibold">Worüber wird gefragt?</h2>
                <p className="mt-1 text-xs" style={{ color: "var(--muted-warm)" }}>Themen der Fragen an den Mira-Assistenten</p>
                {stats.topics.length === 0 && <p className="mt-6 text-sm" style={{ color: "var(--muted-warm)" }}>Noch keine Chat-Fragen.</p>}
                <div className="mt-5 space-y-4">
                  {stats.topics.map((t) => (
                    <div key={t.topic} data-testid={`topic-row-${t.topic}`}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">{t.topic}</span>
                        <span style={{ color: "var(--muted-warm)" }}>{t.count}</span>
                      </div>
                      <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full" style={{ background: "var(--surface-secondary)" }}>
                        <div
                          className="h-full rounded-full transition-[width] duration-700"
                          style={{ width: `${Math.max(4, Math.round((t.count / (stats.topics[0]?.count || 1)) * 100))}%`, background: "var(--camel)" }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border p-6" style={{ borderColor: "var(--line)" }} data-testid="stats-top-questions">
                <h2 className="font-serif-display text-xl font-semibold">Häufigste Fragen</h2>
                <p className="mt-1 text-xs" style={{ color: "var(--muted-warm)" }}>Top-Fragen an den Mira-Assistenten</p>
                {stats.top_questions.length === 0 && <p className="mt-6 text-sm" style={{ color: "var(--muted-warm)" }}>Noch keine Chat-Fragen.</p>}
                <ol className="mt-5 space-y-3">
                  {stats.top_questions.map((q, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm" data-testid={`top-question-${i}`}>
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white" style={{ background: i < 3 ? "var(--camel)" : "var(--gold)" }}>
                        {i + 1}
                      </span>
                      <span className="flex-1 leading-relaxed">{q.question}</span>
                      <span className="whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium" style={{ background: "var(--rose-soft)", color: "var(--camel)" }}>
                        {q.count}×
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            <div className="mt-6 rounded-2xl border p-6" style={{ borderColor: "var(--line)" }} data-testid="stats-languages">
              <h2 className="font-serif-display text-xl font-semibold">Sprachen</h2>
              <p className="mt-1 text-xs" style={{ color: "var(--muted-warm)" }}>Aus welchen Sprachen Chat-Fragen und Newsletter-Anmeldungen kommen</p>
              {(!stats.languages || stats.languages.length === 0) && <p className="mt-6 text-sm" style={{ color: "var(--muted-warm)" }}>Noch keine Daten.</p>}
              <div className="mt-5 grid gap-x-8 gap-y-4 sm:grid-cols-2">
                {(stats.languages || []).map((l) => {
                  const max = Math.max(...stats.languages.map((x) => x.questions + x.signups), 1);
                  return (
                    <div key={l.lang} data-testid={`lang-row-${l.lang}`}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2 font-medium">
                          <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase" style={{ background: "var(--rose-soft)", color: "var(--camel)" }}>{l.lang === "unbekannt" ? "–" : l.lang}</span>
                          {LANG_LABELS[l.lang] || l.lang}
                        </span>
                        <span className="text-xs" style={{ color: "var(--muted-warm)" }}>{l.questions} Fragen · {l.signups} Anmeldungen</span>
                      </div>
                      <div className="mt-1.5 flex h-2 w-full gap-0.5 overflow-hidden rounded-full" style={{ background: "var(--surface-secondary)" }}>
                        <div className="h-full transition-[width] duration-700" style={{ width: `${Math.round((l.questions / max) * 100)}%`, background: "var(--camel)" }} />
                        <div className="h-full transition-[width] duration-700" style={{ width: `${Math.round((l.signups / max) * 100)}%`, background: "var(--gold)" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-5 flex items-center gap-5 text-xs" style={{ color: "var(--muted-warm)" }}>
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: "var(--camel)" }} /> Chat-Fragen</span>
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: "var(--gold)" }} /> Newsletter-Anmeldungen</span>
              </div>
            </div>
          </div>
        )}

        {tab === "leads" && leads && (
          <div className="mt-7 overflow-x-auto rounded-2xl border" style={{ borderColor: "var(--line)" }} data-testid="admin-leads-table">
            <table className="w-full text-left text-sm">
              <thead style={{ background: "var(--surface-secondary)" }}>
                <tr>
                  {["Datum", "Name", "Boutique", "E-Mail", "Plan", "Nachricht", "Status", ""].map((h) => (
                    <th key={h} className="px-4 py-3 text-xs font-bold uppercase" style={{ letterSpacing: "0.08em", color: "var(--muted-warm)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleLeads.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center" style={{ color: "var(--muted-warm)" }}>{onlyOpen ? "Keine offenen Anfragen. ✦" : "Noch keine Anfragen."}</td></tr>
                )}
                {visibleLeads.map((l) => (
                  <tr key={l.id} className="border-t align-top" style={{ borderColor: "var(--line)" }}>
                    <td className="whitespace-nowrap px-4 py-3 text-xs" style={{ color: "var(--muted-warm)" }}>{fmt(l.created_at)}</td>
                    <td className="px-4 py-3 font-medium">{l.name}</td>
                    <td className="px-4 py-3">{l.boutique}</td>
                    <td className="px-4 py-3"><a href={`mailto:${l.email}`} style={{ color: "var(--camel)" }}>{l.email}</a></td>
                    <td className="px-4 py-3">
                      {l.plan ? <span className="rounded-full px-2.5 py-1 text-xs font-medium" style={{ background: "var(--rose-soft)" }}>{l.plan}</span> : "–"}
                    </td>
                    <td className="max-w-xs px-4 py-3 text-xs leading-relaxed" style={{ color: "var(--muted-warm)" }}>{l.message}</td>
                    <td className="px-4 py-3"><StatusPill kind="leads" item={l} /></td>
                    <td className="px-4 py-3"><TrashBtn kind="leads" id={l.id} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "newsletter" && subs && (
          <div className="mt-7 overflow-x-auto rounded-2xl border" style={{ borderColor: "var(--line)" }} data-testid="admin-newsletter-table">
            <table className="w-full text-left text-sm">
              <thead style={{ background: "var(--surface-secondary)" }}>
                <tr>
                  <th className="px-4 py-3 text-xs font-bold uppercase" style={{ letterSpacing: "0.08em", color: "var(--muted-warm)" }}>Datum</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase" style={{ letterSpacing: "0.08em", color: "var(--muted-warm)" }}>E-Mail</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {subs.subscribers.length === 0 && (
                  <tr><td colSpan={3} className="px-4 py-8 text-center" style={{ color: "var(--muted-warm)" }}>Noch keine Anmeldungen.</td></tr>
                )}
                {subs.subscribers.map((s) => (
                  <tr key={s.id} className="border-t" style={{ borderColor: "var(--line)" }}>
                    <td className="whitespace-nowrap px-4 py-3 text-xs" style={{ color: "var(--muted-warm)" }}>{fmt(s.created_at)}</td>
                    <td className="px-4 py-3 font-medium">{s.email}</td>
                    <td className="px-4 py-3"><TrashBtn kind="newsletter" id={s.id} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {tab === "contacts" && contacts && (
          <div className="mt-7 overflow-x-auto rounded-2xl border" style={{ borderColor: "var(--line)" }} data-testid="admin-contacts-table">
            <table className="w-full text-left text-sm">
              <thead style={{ background: "var(--surface-secondary)" }}>
                <tr>
                  {["Datum", "Name", "E-Mail", "Betreff", "Nachricht", "Status", ""].map((h) => (
                    <th key={h} className="px-4 py-3 text-xs font-bold uppercase" style={{ letterSpacing: "0.08em", color: "var(--muted-warm)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleContacts.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center" style={{ color: "var(--muted-warm)" }}>{onlyOpen ? "Keine offenen Kontaktanfragen. ✦" : "Noch keine Kontaktanfragen."}</td></tr>
                )}
                {visibleContacts.map((c) => (
                  <tr key={c.id} className="border-t align-top" style={{ borderColor: "var(--line)" }}>
                    <td className="whitespace-nowrap px-4 py-3 text-xs" style={{ color: "var(--muted-warm)" }}>{fmt(c.created_at)}</td>
                    <td className="px-4 py-3 font-medium">{c.name}</td>
                    <td className="px-4 py-3"><a href={`mailto:${c.email}`} style={{ color: "var(--camel)" }}>{c.email}</a></td>
                    <td className="px-4 py-3">
                      {c.subject ? <span className="rounded-full px-2.5 py-1 text-xs font-medium" style={{ background: "var(--rose-soft)" }}>{c.subject}</span> : "–"}
                    </td>
                    <td className="max-w-xs px-4 py-3 text-xs leading-relaxed" style={{ color: "var(--muted-warm)" }}>{c.message}</td>
                    <td className="px-4 py-3"><StatusPill kind="contacts" item={c} /></td>
                    <td className="px-4 py-3"><TrashBtn kind="contacts" id={c.id} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {tab === "chats" && chats && (
          <div className="mt-7 space-y-4" data-testid="admin-chats-list">
            {chats.chats.length === 0 && (
              <p className="rounded-2xl border px-5 py-8 text-center text-sm" style={{ borderColor: "var(--line)", color: "var(--muted-warm)" }}>Noch keine Chat-Gespräche.</p>
            )}
            {chats.chats.map((c) => (
              <div key={c.session_id} className="rounded-2xl border" style={{ borderColor: "var(--line)" }} data-testid={`admin-chat-${c.session_id}`}>
                <button
                  onClick={() => setOpenChat(openChat === c.session_id ? null : c.session_id)}
                  data-testid={`admin-chat-toggle-${c.session_id}`}
                  className="flex w-full flex-wrap items-center gap-3 px-5 py-4 text-left"
                >
                  <span className="text-xs" style={{ color: "var(--muted-warm)" }}>{fmt(c.last_at)}</span>
                  <span className="rounded-full px-2.5 py-1 text-[10px] font-bold uppercase" style={{ background: "var(--rose-soft)", color: "var(--camel)" }}>{c.lang || "de"}</span>
                  <span className="text-sm font-medium">{c.messages.length} Nachrichten</span>
                  <span className="ml-auto flex-1 truncate text-right text-xs sm:max-w-md" style={{ color: "var(--muted-warm)" }}>
                    {c.messages[0]?.content}
                  </span>
                </button>
                {openChat === c.session_id && (
                  <div className="space-y-2.5 border-t px-5 py-4" style={{ borderColor: "var(--line)" }} data-testid={`admin-chat-transcript-${c.session_id}`}>
                    {c.messages.map((m, i) => (
                      <div
                        key={i}
                        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "ml-auto text-white" : ""}`}
                        style={m.role === "user" ? { background: "var(--camel)" } : { background: "var(--surface-secondary)" }}
                      >
                        {m.content}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {tab === "billing" && <BillingTab adminKey={key} />}
        {tab === "ads" && <AdsTab adminKey={key} />}
        {tab === "aeo" && <AeoTab adminKey={key} />}
        {tab === "magazine" && <MagazineTab adminKey={key} fmt={fmt} />}
      </div>
    </main>
  );
}
